import { isInventoryMeterMountType } from '@enmon/commons/lib/tools/api/inventory-meter';
import { isOutputTechnology as isCommonsOutputTechnology } from '@enmon/commons/lib/tools/types';
import {
  type InventorizationContext,
  readInventorizationContext,
} from '@/features/inventorization/domain/inventorization-context';
import {
  type InventorizationIdentity,
  readCommodity,
  readInventorizationIdentity,
  readMeterPlacementSelection,
} from '@/features/inventorization/domain/inventorization-identity';
import type {
  InventorizationMainReading,
  InventorizationRegisterReading,
} from '@/features/inventorization/domain/inventorization-reading';
import {
  type InventorizationTechnicalDetails,
  isMeterAccessibilitySelection,
  normalizeTechnicalDetails,
  readAccessibilitySelectionValue,
  readInventorizationTechnicalDetails,
} from '@/features/inventorization/domain/inventorization-technical';
import {
  createResolvedInventorizationValueMap,
  isEffectiveResolvedInventorizationValue,
  type ResolvedInventorizationValue,
  readResolvedInventorizationRegisters,
} from '@/features/inventorization/domain/review/inventorization-value-resolver';
import {
  INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_LOCATION_TECHNICAL_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_NOTE_REVIEW_FIELD_CONFIG,
  INVENTORIZE_REGISTER_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_REMOTE_READING_REVIEW_FIELD_CONFIGS,
  type InventorizeStaticReviewFieldConfig,
} from '@/features/inventorization/domain/review/inventorize-review-field-config';
import {
  createInventorizeRegisterReviewFieldId,
  INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS,
  readInventorizeTechnicalDetailReviewFieldKey,
} from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { CaptureSession } from '@/shared/capture/capture-session';
import { getObisOptionsForCommodity } from '@/shared/meters/register-metadata';

export function readEffectiveInventorizationContext(session: CaptureSession): InventorizationContext {
  const context = readInventorizationContext(session);
  const values = createResolvedInventorizationValueMap(session);

  return {
    ...context,
    ...readEffectiveConfigPatch<InventorizationContext>(values, INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS),
  };
}

export function readEffectiveInventorizationIdentity(session: CaptureSession): InventorizationIdentity {
  const identity = readInventorizationIdentity(session);
  const values = createResolvedInventorizationValueMap(session);
  const commodity = readEffectiveConfigValue(values, INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS, 'commodity');
  const newMeter = readEffectiveConfigValue(values, INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS, 'newMeter');
  const placement = readEffectiveConfigValue(values, INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS, 'placement');

  return {
    ...identity,
    ...readEffectiveConfigPatch<InventorizationIdentity>(values, INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS),
    commodity: readCommodity(commodity) || identity.commodity,
    newMeter:
      newMeter === 'Yes' || newMeter === 'true'
        ? true
        : newMeter === 'No' || newMeter === 'false'
          ? false
          : identity.newMeter,
    placement: readMeterPlacementSelection(placement) || identity.placement,
  };
}

export function readEffectiveInventorizationMainReading(session: CaptureSession): InventorizationMainReading {
  const [register] = readEffectiveInventorizationRegisters(session);

  return {
    counter: register?.counter ?? '',
    obis: register?.obis ?? '',
    tariff: register?.tariff ?? '',
    unit: register?.unit ?? '',
  };
}

export function readEffectiveInventorizationRegisters(session: CaptureSession): InventorizationRegisterReading[] {
  const registers = readResolvedInventorizationRegisters(session);
  const values = createResolvedInventorizationValueMap(session);
  const commodity = readEffectiveInventorizationIdentity(session).commodity;
  const allowedObisValues = getObisOptionsForCommodity(commodity).map(option => option.value);

  return registers.map(register => ({
    ...register,
    ...Object.fromEntries(
      INVENTORIZE_REGISTER_REVIEW_FIELD_CONFIGS.map(config => {
        const value =
          readEffectiveFieldValue(values, createInventorizeRegisterReviewFieldId(register.id, config.field)) ??
          register[config.field];

        return [
          config.field,
          config.field === INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS.obis && !allowedObisValues.includes(value)
            ? ''
            : value,
        ];
      }),
    ),
  }));
}

export function readEffectiveInventorizationTechnicalDetails(session: CaptureSession): InventorizationTechnicalDetails {
  const technical = readInventorizationTechnicalDetails(session);
  const detail = { ...technical.detail };
  const values = createResolvedInventorizationValueMap(session);
  const technicalConfigs = [
    ...INVENTORIZE_LOCATION_TECHNICAL_REVIEW_FIELD_CONFIGS,
    ...INVENTORIZE_REMOTE_READING_REVIEW_FIELD_CONFIGS,
    INVENTORIZE_NOTE_REVIEW_FIELD_CONFIG,
  ];
  const accessibility = readAccessibilitySelectionValue(
    readEffectiveConfigValue(values, technicalConfigs, 'accessibility'),
  );
  const mountTechnology = readEffectiveConfigValue(values, technicalConfigs, 'mountTechnology');
  const mountType = readEffectiveConfigValue(values, technicalConfigs, 'mountType');

  for (const field of values.values()) {
    const key = readInventorizeTechnicalDetailReviewFieldKey(field.id);

    if (!(key && isEffectiveResolvedInventorizationValue(field))) {
      continue;
    }

    detail[key] = field.value;
  }

  return normalizeTechnicalDetails({
    ...technical,
    accessibility: isMeterAccessibilitySelection(accessibility) ? accessibility : technical.accessibility,
    detail,
    monitoredEntity: readEffectiveConfigValue(values, technicalConfigs, 'monitoredEntity') ?? technical.monitoredEntity,
    mountTechnology:
      mountTechnology === '' || isCommonsOutputTechnology(mountTechnology)
        ? mountTechnology
        : technical.mountTechnology,
    mountType: isInventoryMeterMountType(mountType) ? mountType : technical.mountType,
    note: readEffectiveConfigValue(values, technicalConfigs, 'note') ?? technical.note,
    remoteReadingOwner:
      readEffectiveConfigValue(values, technicalConfigs, 'remoteReadingOwner') ?? technical.remoteReadingOwner,
    remoteReadingSerial:
      readEffectiveConfigValue(values, technicalConfigs, 'remoteReadingSerial') ?? technical.remoteReadingSerial,
  });
}

function readEffectiveConfigPatch<T>(
  values: Map<string, ResolvedInventorizationValue>,
  configs: readonly InventorizeStaticReviewFieldConfig[],
) {
  return Object.fromEntries(
    configs.flatMap(config => {
      const value = readEffectiveFieldValue(values, config.id);

      return value === undefined ? [] : [[config.key, value]];
    }),
  ) as Partial<T>;
}

function readEffectiveConfigValue(
  values: Map<string, ResolvedInventorizationValue>,
  configs: readonly InventorizeStaticReviewFieldConfig[],
  key: string,
) {
  const config = configs.find(item => item.key === key);

  return config ? readEffectiveFieldValue(values, config.id) : undefined;
}

function readEffectiveFieldValue(values: Map<string, ResolvedInventorizationValue>, fieldId: string) {
  const value = values.get(fieldId);

  return isEffectiveResolvedInventorizationValue(value) ? value.value : undefined;
}
